import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { TextInput } from '@/components/form/fields/text/TextInput';
import { Card } from '@/components/sections/Card';
import { sort } from '@/components/table/sortHelper';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/tabs/Tabs';
import SiPlusCircle from '@/icons/SiPlusCircle';
import SiSearch from '@/icons/SiSearch';
import BreadcrumbPageHeader from '@/layouts/BreadcrumbPageHeader';
import { Container } from '@/layouts/main/Main';
import { PurchaseOrderBundle } from '@/services/api/contracts/catalog/bundles';
import {
  useCompletedPurchaseOrders,
  useDraftPurchaseOrders,
  useInProgressPurchaseOrders
} from '@/services/purchaseOrders/usePurchaseOrders';
import { useSearch } from '@/utils/search';
import { FC, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PurchaseOrderCard } from './PurchaseOrderCard';
import PurchaseOrderLandingPageFilters, {
  PurchaseOrderLandingPageFilterOptions
} from './filter/PurchaseOrderLandingPageFilters';

interface LocationState {
  completed?: boolean;
}

export const PurchaseOrderLandingPage: FC = () => {
  const navigate = useNavigate();
  const { companyId, partnerId } = useParams<{ companyId: string; partnerId: string }>();

  if (!companyId || !partnerId) {
    throw Error('Missing page parameter');
  }

  const breadcrumbs: NavPage[] = [
    {
      name: 'Orders / Quotes',
      to: ''
    }
  ];

  const draftOrders = useDraftPurchaseOrders(partnerId, companyId);
  const inProgressOrders = useInProgressPurchaseOrders(partnerId, companyId);
  const completedOrders = useCompletedPurchaseOrders(partnerId, companyId);

  const firstVisit = draftOrders.length + inProgressOrders.length + completedOrders.length === 0;

  const [searchQuery, setSearchQuery] = useState<string>('');

  const filteredDraftOrdersByName = useSearch(draftOrders, ['name'], searchQuery);
  const filteredInProgressOrdersByName = useSearch(inProgressOrders, ['name'], searchQuery);
  const filteredCompletedOrdersByName = useSearch(completedOrders, ['name'], searchQuery);

  const defaultTab = 'draft';

  const [sortOptions, setSortOptions] = useState<PurchaseOrderLandingPageFilterOptions | undefined>();

  const onSort = (options: PurchaseOrderLandingPageFilterOptions) => {
    setSortOptions(options);
  };

  const sortedData = useMemo(() => {
    const sortOrders = (orders: PurchaseOrderBundle[]) => {
      if (!sortOptions) return orders;

      const sortingRules = [
        { field: 'createdOn', order: sortOptions?.dateCreated },
        { field: 'submittedOn', order: sortOptions?.dateSubmitted },
        { field: 'updatedOn', order: sortOptions?.dateUpdated }
      ].filter(rule => rule.order);

      return sortingRules.reduce((sorted, { field, order }) => {
        return sort(sorted as PurchaseOrderBundle[], field, order!);
      }, orders);
    };

    return {
      draftSortedOrders: sortOrders(filteredDraftOrdersByName),
      inProgressSortedOrders: sortOrders(filteredInProgressOrdersByName),
      completedSortedOrders: sortOrders(filteredCompletedOrdersByName)
    };
  }, [sortOptions, filteredDraftOrdersByName, filteredInProgressOrdersByName, filteredCompletedOrdersByName]);

  return (
    <>
      <BreadcrumbPageHeader breadcrumbs={breadcrumbs}>
        <div className="flex justify-between gap-5 items-center font-bold">
          <h3 className="text-3xl">Orders / Quotes</h3>
          {!firstVisit && (
            <Button variant={ButtonVariant.Primary} className="h-fit" onClick={() => navigate('create/poCatalogue')}>
              <SiPlusCircle className="text-xl" />
              <span>Create</span>
            </Button>
          )}
        </div>
      </BreadcrumbPageHeader>
      <div className="flex-1 overflow-y-auto">
        <Container animate="pop" className="w-full py-8">
          {firstVisit ? (
            <Card className="p-5 flex items-center justify-center gap-5 flex-col max-w-4xl mx-auto">
              <Button variant={ButtonVariant.Primary} onClick={() => navigate('create/poCatalogue')}>
                <span>Create</span>
              </Button>
            </Card>
          ) : (
            <Tabs defaultValue={defaultTab}>
              <div className="space-y-10">
                <div>
                  <TabsList>
                    <TabsTrigger value="draft">{`Draft (${sortedData.draftSortedOrders.length})`}</TabsTrigger>
                    <TabsTrigger value="inProgress">{`In Progress (${sortedData.inProgressSortedOrders.length})`}</TabsTrigger>
                    <TabsTrigger value="completed">{`Completed (${sortedData.completedSortedOrders.length})`}</TabsTrigger>
                  </TabsList>
                </div>

                <div className="space-y-5">
                  <PurchaseOrderLandingPageFilters onSortCallback={onSort} />

                  <TextInput
                    id="filter"
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder={'Search'}
                    prefix={<SiSearch className="text-xl" />}
                  />
                  <TabsContent value="draft">
                    <div className="space-y-5">
                      {sortedData.draftSortedOrders.map(order => (
                        <PurchaseOrderCard key={order.id} order={order} />
                      ))}
                      {sortedData.draftSortedOrders.length === 0 && (
                        <div>
                          {searchQuery
                            ? 'There are no draft orders / quotes matching your search term.'
                            : 'You have no draft orders / quotes.'}
                        </div>
                      )}
                    </div>
                  </TabsContent>
                  <TabsContent value="inProgress">
                    <div className="space-y-5">
                      {sortedData.inProgressSortedOrders.map(order => (
                        <PurchaseOrderCard key={order.id} order={order} submitted />
                      ))}
                      {sortedData.inProgressSortedOrders.length === 0 && (
                        <div>
                          {searchQuery
                            ? 'There are no in progress orders / quotes matching your search term.'
                            : 'You have no in progress orders / quotes.'}
                        </div>
                      )}
                    </div>
                  </TabsContent>
                  <TabsContent value="completed">
                    <div className="space-y-5">
                      {sortedData.completedSortedOrders.map(order => (
                        <PurchaseOrderCard key={order.id} order={order} submitted />
                      ))}
                      {sortedData.completedSortedOrders.length === 0 && (
                        <div>
                          {searchQuery
                            ? 'There are no completed orders / quotes matching your search term.'
                            : 'You have no completed orders / quotes.'}
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </div>
              </div>
            </Tabs>
          )}
        </Container>
      </div>
    </>
  );
};
